import { useState } from "react";
import type { PurchaseOrderDto, StockLocationDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

interface LineInput {
  poLineId: string;
  quantity: string;
  locationId: string;
}

export function ReceiveForm({
  order,
  locations,
  onReceived,
}: {
  order: PurchaseOrderDto;
  locations: StockLocationDto[];
  onReceived(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [inputs, setInputs] = useState<LineInput[]>(
    order.lines
      .filter((l) => l.outstanding !== "0")
      .map((l) => ({
        poLineId: l.id,
        quantity: l.outstanding,
        locationId: locations[0]?.id ?? "",
      }))
  );
  const [supplierReference, setSupplierReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.receivePurchaseOrder(order.id, {
        supplierReference: supplierReference || "",
        lines: inputs
          .filter((i) => i.quantity && Number(i.quantity) > 0)
          .map((i) => ({
            poLineId: i.poLineId,
            quantity: i.quantity,
            locationId: i.locationId || "",
          })),
      });
      push(t("purchasing.receiptRecorded"));
      onReceived();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded border border-line p-3">
      {inputs.map((input, idx) => {
        const line = order.lines.find((l) => l.id === input.poLineId)!;
        return (
          <div key={idx} className="grid items-end gap-2 sm:grid-cols-3">
            <span className="text-sm text-ink">
              {line.description}{" "}
              <span className="text-ink-muted">
                {t("purchasing.outstandingNote", { outstanding: line.outstanding })}
              </span>
            </span>
            <TextInput
              label={t("common.fields.quantity")}
              value={input.quantity}
              onChange={(e) =>
                setInputs((l) =>
                  l.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                )
              }
            />
            <SelectInput
              label={t("purchasing.location")}
              options={locations.map((l) => ({ value: l.id, label: l.name }))}
              value={input.locationId}
              onChange={(e) =>
                setInputs((l) =>
                  l.map((x, i) => (i === idx ? { ...x, locationId: e.target.value } : x))
                )
              }
            />
          </div>
        );
      })}
      <TextInput
        label={t("purchasing.supplierReference")}
        value={supplierReference}
        onChange={(e) => setSupplierReference(e.target.value)}
        hint={t("purchasing.supplierReferenceHint")}
      />
      <Button variant="primary" disabled={busy} onClick={submit}>
        {busy ? t("purchasing.receiving") : t("purchasing.recordReceipt")}
      </Button>
    </div>
  );
}
